import {
  Box,
  FieldSwitch,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  Form,
  FormErrors,
  getFormValues,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { selectSortedQueuesByName } from 'app/utils/reselect';
import {
  FlowNodeTypes,
  OptionT,
} from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { QueuePicker, QueuePickerOption } from 'components/shared/queuePicker';
import { getRequestID } from 'components/shared/util/request';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { durationConverter } from 'utils/duration';

import type { FlowEmailNodeQueueT, Payload } from '../nodeTypes';

import { TimeoutFields } from './queue/timeoutFields';
import styles from './queue.module.scss';

type StateProps = ReturnType<typeof mapStateToProps>;

type OwnProps = FlowManagerEditModalComponentT<FlowEmailNodeQueueT>;
type FlowEmailQueueModalReduxFormT = InjectedFormProps<FormValues, Props>;

type Validation = {
  queue?: {
    value: string;
  };
  queue_timeout?: {
    timeout_in_seconds: string;
  };
};

type Props = OwnProps & StateProps;

type TimeoutInSecondsObject = {
  dd: string;
  hh: string;
  mm: string;
  ss: string;
};

type FormValues = {
  queue: null | OptionT | { value: QueuePickerOption };
  queue_timeout?: {
    timeout_in_seconds: TimeoutInSecondsObject;
  };
};

const validate = (values: FormValues): FormErrors => {
  const errors: Validation = {};

  if (!values) {
    return errors;
  }

  if (!values.queue?.value) {
    errors.queue = {
      value: 'Selecting a queue is required',
    };
  }

  if (values.queue_timeout && !values.queue_timeout.timeout_in_seconds) {
    errors.queue_timeout = {
      timeout_in_seconds: 'Please set a timeout value',
    };
  }

  return errors;
};

class QueueModalImpl extends PureComponent<
  Props & FlowEmailQueueModalReduxFormT
> {
  @boundMethod
  handleSaveClick(values: FormValues): void {
    const { queuesState } = this.props;
    let selectedQueue: null | OptionT = null;

    // Find queue in queues.
    if (typeof values.queue?.value === 'string') {
      for (const queue of queuesState) {
        if (queue.id === values.queue.value) {
          selectedQueue = {
            value: queue.id,
            label: queue.name,
          };
        }
      }
    }

    /*
     * With the new modals the QueuePicker component returns an object.
     * Once the feature flag is lifted, this condition can be simplified
     * and the above string comparison removed.
     */
    if (typeof values.queue?.value === 'object') {
      const { value } = values.queue;
      const queue = value as QueuePickerOption;

      selectedQueue = {
        value: queue.id,
        label: queue.name,
      };
    }

    const payload: Payload = {
      ...this.props.node.payload,
      queue: selectedQueue,
    };

    const timeoutInSeconds = values.queue_timeout?.timeout_in_seconds;

    if (timeoutInSeconds) {
      const { ss, mm, hh, dd } = timeoutInSeconds;
      const totalSecounds = durationConverter.combineTimeUnits(
        dd ? parseInt(dd, 10) : 0,
        hh ? parseInt(hh, 10) : 0,
        mm ? parseInt(mm, 10) : 0,
        ss ? parseInt(ss, 10) : 0,
      );

      if (totalSecounds > 0) {
        payload.queue_timeout = {
          timeout_in_seconds: totalSecounds,
          timeout_node_id:
            this.props.node.payload?.queue_timeout?.timeout_node_id ||
            getRequestID(),
        };
      }
    } else {
      delete payload.queue_timeout;
    }

    const nodeOptions: FlowEmailNodeQueueT = {
      ...this.props.node,
      badges: [],
      childIds: [],
      icon: 'i-queue',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.QUEUE,
      overlay: [],
      payload,
      text: `Queue ${
        typeof selectedQueue?.label === 'string'
          ? `(${selectedQueue.label})`
          : ''
      }`,
    };

    this.props.replaceNode(nodeOptions);
    this.props.triggerClose();
  }

  render() {
    const {
      formValues,
      handleSubmit,
      node,
      nodeLabel,
      triggerClose,
      dirty: isChanged,
    } = this.props;
    const { payload } = node;

    return (
      <Box width="600px">
        <ModalHead title={nodeLabel} onClose={triggerClose}>
          <div className={styles.description}>
            The queue is where you’d like your incoming conversations to end up
            based on the queues you’ve set up. Haven’t made any custom queues
            yet? Go to Settings &#62; Queues &#62; Add queue
          </div>
        </ModalHead>
        <Form onSubmit={handleSubmit(this.handleSaveClick)}>
          <ModalBody>
            <Field
              component={QueuePicker}
              label="Select queue"
              name="queue.value"
              placeholder="No queue selected"
              selectedQueue={payload?.queue}
              required
            />
            <FormGrid className={styles.timeout}>
              <FormField className={styles.timeoutField}>
                <label className={styles.timeoutLabel} htmlFor="fieldSwitch">
                  Timeout
                </label>
                <Field
                  checked={!!formValues?.queue_timeout}
                  className={styles.fieldSwitch}
                  component={FieldSwitch}
                  id="fieldSwitch"
                  label="Outbound call recording"
                  name="queue_timeout"
                />
              </FormField>
              {formValues?.queue_timeout && (
                <FormField>
                  <Field
                    name="queue_timeout.timeout_in_seconds"
                    component={TimeoutFields}
                  />
                </FormField>
              )}
            </FormGrid>
          </ModalBody>
          <ModalFoot
            confirmOnClick={handleSubmit(this.handleSaveClick)}
            confirmName={getConfirmButtonVerb(node)}
            confirmProps={{ disabled: !isChanged }}
            cancelOnClick={triggerClose}
          />
        </Form>
      </Box>
    );
  }
}

const mapStateToProps = (state: RootReducerStateT, ownProps: OwnProps) => {
  const payload: Payload = {
    queue: null, // will be overridden by node.payload if present
    ...ownProps.node.payload,
  };
  const initialValues: FormValues = {
    ...payload,
    queue_timeout: undefined, // overrides incompatible queue_timeout in payload
  };

  const timeoutInSeconds = payload?.queue_timeout?.timeout_in_seconds;

  if (timeoutInSeconds) {
    const { days, hours, minutes, seconds } = durationConverter.splitTimeUnits(
      timeoutInSeconds,
      true,
    );

    initialValues.queue_timeout = {
      timeout_in_seconds: {
        dd: (days ?? 0).toString(),
        hh: hours.toString(),
        mm: minutes.toString(),
        ss: seconds.toString(),
      },
    };
  }

  return {
    queuesState: selectSortedQueuesByName(state),
    initialValues,
    formValues: getFormValues('emailFlowQueueForm')(state) as FormValues,
  };
};

export const QueueModal = connect<
  StateProps,
  Record<string, never>,
  OwnProps,
  RootReducerStateT
>(mapStateToProps)(
  reduxForm<FormValues, Props>({
    form: 'emailFlowQueueForm', // a unique identifier for this form
    enableReinitialize: true,
    touchOnBlur: false,
    validate,
  })(QueueModalImpl),
);
